import React from "react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { moduleMeta } from "@/lib/icons";

export function SectionTitle({ children, className }) {
  return (
    <div className={cn("text-xs font-semibold tracking-wider uppercase text-muted-foreground", className)}>
      {children}
    </div>
  );
}

export function ModuleBadge({ moduleKey, name, licensed = true, showLock = false, testid }) {
  const meta = moduleMeta(moduleKey);
  const Icon = meta.Icon;
  return (
    <span
      data-testid={testid}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium",
        licensed ? meta.chip : "border-border bg-muted/40 text-muted-foreground/70"
      )}
    >
      {licensed ? <Icon className="h-3.5 w-3.5" /> : <Lock className="h-3 w-3" />}
      {name || moduleKey}
    </span>
  );
}

export function StatCard({ label, value, icon: Icon, hint, accent = "text-primary", testid }) {
  return (
    <Card data-testid={testid} className="p-5 bg-card border-border" style={{ boxShadow: "var(--shadow-soft)" }}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
          <div className="mt-2 text-3xl font-display font-semibold tabular-nums">{value}</div>
          {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
        </div>
        {Icon && (
          <div className={cn("h-10 w-10 rounded-xl bg-secondary grid place-items-center", accent)}>
            <Icon className="h-5 w-5" />
          </div>
        )}
      </div>
    </Card>
  );
}

export function ScoreBar({ score = 0 }) {
  const pct = Math.round((score || 0) * 100);
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-20 rounded-full bg-muted overflow-hidden">
        <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[11px] text-muted-foreground tabular-nums">{pct}%</span>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, subtitle, action, testid }) {
  return (
    <div data-testid={testid} className="flex flex-col items-center justify-center text-center py-16 px-6">
      {Icon && (
        <div className="h-14 w-14 rounded-2xl bg-secondary grid place-items-center mb-4">
          <Icon className="h-7 w-7 text-primary" />
        </div>
      )}
      <div className="font-display font-semibold text-lg">{title}</div>
      {subtitle && <div className="mt-1.5 text-sm text-muted-foreground max-w-md">{subtitle}</div>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

// Parse assistant text and render [module/sheet/record_id] as clickable chips.
const CITATION_RE = /\[([a-zA-Z_]+)\/([^/\]]+)\/([^\]]+)\]/g;

export function renderWithCitations(text, onCite) {
  if (!text) return null;
  const nodes = [];
  let lastIndex = 0;
  let m;
  let i = 0;
  CITATION_RE.lastIndex = 0;
  while ((m = CITATION_RE.exec(text)) !== null) {
    if (m.index > lastIndex) {
      nodes.push(<span key={`t-${i}`}>{text.slice(lastIndex, m.index)}</span>);
    }
    const [full, moduleKey, sheet, recordId] = m;
    const citation = `${moduleKey}/${sheet}/${recordId}`;
    const meta = moduleMeta(moduleKey);
    nodes.push(
      <button
        key={`c-${i}`}
        type="button"
        onClick={() => onCite && onCite(citation)}
        data-testid={`citation-chip-${moduleKey}-${String(recordId).slice(0, 20)}`}
        className={cn(
          "inline-flex items-center align-middle mx-0.5 rounded-md border px-1.5 py-0.5 font-mono text-[11px] transition-colors hover:brightness-125",
          meta.chip
        )}
        title={citation}
      >
        {sheet}/{recordId}
      </button>
    );
    lastIndex = m.index + full.length;
    i += 1;
  }
  if (lastIndex < text.length) {
    nodes.push(<span key="t-end">{text.slice(lastIndex)}</span>);
  }
  return nodes;
}

export function GuaranteePill({ label }) {
  return (
    <Badge variant="outline" className="border-primary/30 bg-primary/10 text-primary gap-1.5 py-1">
      {label}
    </Badge>
  );
}
